import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import {
  AppsOutlined,
  AssignmentOutlined,
  CalendarTodayOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DeleteForeverOutlined,
  ExpandMoreOutlined,
  FilterAltOutlined,
  GridViewOutlined,
  HourglassEmptyOutlined,
  PaymentsOutlined,
  PersonOutlineOutlined,
  RefreshOutlined,
  SearchOutlined,
  TableRowsOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";

import "./applicant-log.css";

const API = process.env.REACT_APP_SERVER_SCRIPT_HOST;

const getApplicantName = (applicant) =>
  [
    applicant?.first_Name || applicant?.personalInfo?.first_Name,
    applicant?.sur_Name || applicant?.personalInfo?.sur_Name,
    applicant?.other_Name || applicant?.personalInfo?.other_Name,
  ]
    .filter(Boolean)
    .join(" ") || "Unnamed Applicant";

const getApplicationNumber = (applicant) =>
  applicant?.application_number ||
  applicant?.admission_number ||
  applicant?.academicInfo?.admission_number ||
  "N/A";

const getProgramme = (applicant) =>
  applicant?.course_of_study ||
  applicant?.programme_of_study ||
  applicant?.academicInfo?.programme ||
  applicant?.academicInfo?.department ||
  "Programme not specified";

const getRegistrationStatus = (applicant) =>
  applicant?.account_status?.registration_status || "pending";

const getPaymentStatus = (applicant) => {
  const status = String(
    applicant?.account_status?.paymentStatus ||
      applicant?.payment_status ||
      applicant?.paymentStatus ||
      ""
  ).toLowerCase();

  if (
    ["success", "successful", "paid", "completed", "complete"].includes(status)
  ) {
    return "paid";
  }

  if (["failed", "cancelled", "canceled"].includes(status)) {
    return "failed";
  }

  return "pending";
};

const getDate = (applicant) =>
  applicant?.registered_date?.date ||
  applicant?.registered_date ||
  applicant?.createdAt ||
  applicant?.created_at ||
  "—";

const getInitials = (applicant) => {
  const name = getApplicantName(applicant);

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
};

function StatusBadge({ status, type = "registration" }) {
  const normalized = String(status || "pending")
    .toLowerCase()
    .replace(/\s+/g, "-");

  let label = status || "Pending";
  let className = normalized;

  if (type === "payment") {
    if (normalized === "paid") label = "Paid";
    else if (normalized === "failed") label = "Failed";
    else label = "Pending";
  }

  if (type === "registration") {
    if (normalized === "completed") label = "Completed";
    else if (normalized === "complete") label = "Completed";
    else if (normalized === "in-progress") label = "In Progress";
    else label = "Pending";
  }

  return (
    <span className={`applicant-status applicant-status--${className}`}>
      <span className="applicant-status__dot" />
      {label}
    </span>
  );
}

function ApplicantAvatar({ applicant, large = false }) {
  const name = getApplicantName(applicant);

  return (
    <div
      className={`applicant-avatar ${
        large ? "applicant-avatar--large" : ""
      }`}
    >
      {applicant?.passport?.url ? (
        <img src={applicant.passport.url} alt={name} />
      ) : (
        <span>{getInitials(applicant)}</span>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, description, variant }) {
  return (
    <article className={`applicant-stat applicant-stat--${variant}`}>
      <div className="applicant-stat__top">
        <div className="applicant-stat__icon">{icon}</div>

        <span className="applicant-stat__eyebrow">
          Admissions
        </span>
      </div>

      <div className="applicant-stat__content">
        <strong>{value ?? 0}</strong>
        <span>{label}</span>
      </div>

      <p>{description}</p>
    </article>
  );
}

function TableView({ applicants, onDelete }) {
  const adminPermission = useSelector(
    (state) => state.adminFunction?.queue?.adminData?.email
  );

  return (
    <section className="applicant-table-card">
      <div className="applicant-table-card__header">
        <div>
          <span className="workspace-section-label">APPLICATION PIPELINE</span>
          <h2>Applicant records</h2>
          <p>
            Review submitted applications and monitor their registration
            progress.
          </p>
        </div>

        <span className="record-count">
          {applicants.length}{" "}
          {applicants.length === 1 ? "record" : "records"}
        </span>
      </div>

      <div className="applicant-table-scroll">
        <table className="applicant-table">
          <thead>
            <tr>
              <th className="table-index">#</th>
              <th>Applicant</th>
              <th>Application</th>
              <th>Programme</th>
              <th>Payment</th>
              <th>Registration</th>
              <th>Submitted</th>
              <th className="table-actions">Actions</th>
            </tr>
          </thead>

          <tbody>
            {applicants.map((applicant, index) => {
              const applicationNumber =
                getApplicationNumber(applicant);

              return (
                <tr key={applicant?._id || applicationNumber || index}>
                  <td className="table-index">
                    {String(index + 1).padStart(2, "0")}
                  </td>

                  <td>
                    <div className="table-applicant">
                      <ApplicantAvatar applicant={applicant} />

                      <div className="table-applicant__identity">
                        <strong>{getApplicantName(applicant)}</strong>

                        <span>
                          {applicant?.gender || "Gender not specified"}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className="application-number">
                      {applicationNumber}
                    </span>
                  </td>

                  <td>
                    <div className="table-programme">
                      <strong>{getProgramme(applicant)}</strong>
                      <span>
                        {applicant?.academicInfo?.department ||
                          "Academic programme"}
                      </span>
                    </div>
                  </td>

                  <td>
                    <StatusBadge
                      status={getPaymentStatus(applicant)}
                      type="payment"
                    />
                  </td>

                  <td>
                    <StatusBadge
                      status={getRegistrationStatus(applicant)}
                    />
                  </td>

                  <td>
                    <div className="table-date">
                      <CalendarTodayOutlined />
                      <span>{getDate(applicant)}</span>
                    </div>
                  </td>

                  <td>
                    <div className="table-row-actions">
                      <Link
                        className="applicant-action applicant-action--view"
                        to={`/admin_profile/applicant_log/${applicationNumber}`}
                        target="_blank"
                        rel="noreferrer"
                        title="View application"
                      >
                        <VisibilityOutlined />
                      </Link>

                      {adminPermission ===
                        "princedikko@gmail.com" && (
                        <button
                          type="button"
                          className="applicant-action applicant-action--delete"
                          onClick={() => onDelete(applicationNumber)}
                          title="Delete applicant"
                        >
                          <DeleteForeverOutlined />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CardView({ applicants, onDelete }) {
  const adminPermission = useSelector(
    (state) => state.adminFunction?.queue?.adminData?.email
  );

  return (
    <section className="applicant-grid">
      {applicants.map((applicant, index) => {
        const applicationNumber =
          getApplicationNumber(applicant);

        return (
          <article
            className="applicant-card"
            key={applicant?._id || applicationNumber || index}
          >
            <div className="applicant-card__top">
              <div className="applicant-card__identity">
                <ApplicantAvatar applicant={applicant} large />

                <div>
                  <h3>{getApplicantName(applicant)}</h3>

                  <span>
                    {applicationNumber}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="card-menu-button"
                aria-label="Applicant options"
              >
                <ExpandMoreOutlined />
              </button>
            </div>

            <div className="applicant-card__divider" />

            <div className="fx-jb space3">
            <div className="applicant-card__programme">
              <span>Programme</span>
              <strong>{getProgramme(applicant)}</strong>
            </div>
            <StatusBadge
                  status={getRegistrationStatus(applicant)}
                />

            </div>



            <div className="applicant-card__meta">
              <div>
                <span>Gender</span>
                <strong>{applicant?.gender || "—"}</strong>
              </div>

              <div>
                <span>Submitted</span>
                <strong>{getDate(applicant)}</strong>
              </div>
            </div>

            {/* <div className="applicant-card__statuses">
              <div>
                <span>Payment</span>
                <StatusBadge
                  status={getPaymentStatus(applicant)}
                  type="payment"
                />
              </div>

              <div>
                <span>Registration</span>
                <StatusBadge
                  status={getRegistrationStatus(applicant)}
                />
              </div>
            </div> */}

            <div className="applicant-card__actions" style={{color: "#fff"}}>
              <Link
                to={`/admin_profile/applicant_log/${applicationNumber}`}
                target="_blank"
                rel="noreferrer"
                className="card-primary-action"
              >
                <VisibilityOutlined />
                View application
              </Link>

              {adminPermission === "princedikko@gmail.com" && (
                <button
                  type="button"
                  className="card-delete-action"
                  onClick={() => onDelete(applicationNumber)}
                >
                  <DeleteForeverOutlined />
                </button>
              )}
            </div>
          </article>
        );
      })}
    </section>
  );
}

function EmptyState({ hasFilters, onReset }) {
  return (
    <section className="applicant-empty">
      <div className="applicant-empty__icon">
        <PersonOutlineOutlined />
      </div>

      <h3>
        {hasFilters
          ? "No applicants match your filters"
          : "No applicant records yet"}
      </h3>

      <p>
        {hasFilters
          ? "Try changing your search or department filter."
          : "Applicant submissions will appear here once they are received."}
      </p>

      {hasFilters && (
        <button type="button" onClick={onReset}>
          <RefreshOutlined />
          Clear filters
        </button>
      )}
    </section>
  );
}

export default function ApplicantLogs() {
  const { enqueueSnackbar } = useSnackbar();

  const [applicants, setApplicants] = useState([]);
  const [counts, setCounts] = useState({
    count: 0,
    pendings: 0,
    completeds: 0,
  });

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [department, setDepartment] = useState("");
  const [view, setView] = useState("grid");

  const fetchApplicants = async (selectedDepartment = "") => {
    try {
      setLoading(true);

      let response;

      if (
        selectedDepartment &&
        selectedDepartment !== "all"
      ) {
        response = await axios.post(`${API}/applicant_logs`, {
          department: selectedDepartment,
        });
      } else {
        response = await axios.get(`${API}/applicant_logs`);
      }

      const result = response?.data || {};

      setApplicants(result.response || []);

      setCounts({
        count: result.count || 0,
        pendings: result.pendings || 0,
        completeds: result.completeds || 0,
      });
    } catch (error) {
      console.error("Applicant logs error:", error);

      enqueueSnackbar(
        "Unable to load applicant records.",
        {
          variant: "error",
          autoHideDuration: 3500,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicants();
  }, []);

  const filteredApplicants = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return applicants;

    return applicants.filter((applicant) => {
      const searchable = [
        getApplicantName(applicant),
        getApplicationNumber(applicant),
        getProgramme(applicant),
        applicant?.gender,
        applicant?.academicInfo?.department,
        applicant?.account_status?.registration_status,
        applicant?.account_status?.paymentStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(search);
    });
  }, [applicants, searchTerm]);

  const handleDepartmentChange = (event) => {
    setDepartment(event.target.value);
  };

  const applyDepartmentFilter = () => {
    fetchApplicants(department);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDepartment("");
    fetchApplicants();
  };

  const handleDelete = async (applicationNumber) => {
    const confirmed = window.confirm(
      `Delete applicant ${applicationNumber}? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      const response = await axios.delete(
        `${API}/delete/applicant/${applicationNumber}`
      );

      setApplicants((previous) =>
        previous.filter(
          (applicant) =>
            getApplicationNumber(applicant) !==
            applicationNumber
        )
      );

      setCounts((previous) => ({
        ...previous,
        count: Math.max((previous.count || 0) - 1, 0),
      }));

      enqueueSnackbar(
        response?.data?.message ||
          "Applicant deleted successfully.",
        {
          variant: "success",
          autoHideDuration: 3000,
        }
      );
    } catch (error) {
      console.error("Delete applicant error:", error);

      enqueueSnackbar(
        "Unable to delete applicant.",
        {
          variant: "error",
          autoHideDuration: 3500,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const hasFilters =
    Boolean(searchTerm.trim()) || Boolean(department);

  return (
    <main className="applicants-log">
      <div className="applicants-log__container">

        {/* Workspace Header */}
        <section className="applicant-workspace-header">
          <div className="workspace-heading">
            <div className="workspace-heading__eyebrow">
              <span />
              ADMISSIONS WORKSPACE
            </div>

            <h1>Applicant Management</h1>

            <p>
              Manage applications, monitor registration progress,
              and review applicant records from one workspace.
            </p>
          </div>

          <div className="workspace-header-actions">
            <button
              type="button"
              className="workspace-refresh"
              onClick={() => fetchApplicants(department)}
              disabled={loading}
            >
              <RefreshOutlined
                className={loading ? "is-spinning" : ""}
              />
              Refresh
            </button>
          </div>
        </section>

        {/* KPI Strip */}
        <section className="applicant-stats">
          <StatCard
            icon={<PersonOutlineOutlined />}
            label="Total applicants"
            value={counts.count}
            description="Applications received"
            variant="primary"
          />

          <StatCard
            icon={<HourglassEmptyOutlined />}
            label="Pending"
            value={counts.pendings}
            description="Awaiting completion"
            variant="warning"
          />

          <StatCard
            icon={<CheckCircleOutlined />}
            label="Completed"
            value={counts.completeds}
            description="Fully registered"
            variant="success"
          />

          <StatCard
            icon={<PaymentsOutlined />}
            label="Visible records"
            value={filteredApplicants.length}
            description="Matching current view"
            variant="neutral"
          />
        </section>

        {/* Controls */}
        <section className="applicant-controls">
          <div className="applicant-search">
            <SearchOutlined />

            <input
              type="search"
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
              placeholder="Search by applicant, application number, programme..."
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <CloseOutlined />
              </button>
            )}
          </div>

          <div className="applicant-filter">
            <FilterAltOutlined />

            <select
              value={department}
              onChange={handleDepartmentChange}
            >
              <option value="">All departments</option>
              <option value="Midwifery">
                Department of Midwifery
              </option>
              <option value="Nursing">
                Department of Nursing
              </option>
            </select>

            <ExpandMoreOutlined />
          </div>

          <button
            type="button"
            className="apply-filter-button"
            onClick={applyDepartmentFilter}
            disabled={loading}
          >
            <SearchOutlined />
            Apply
          </button>

          {hasFilters && (
            <button
              type="button"
              className="clear-filter-button"
              onClick={clearFilters}
            >
              Clear
            </button>
          )}

          <div className="view-switcher">
            <button
              type="button"
              className={
                view === "table"
                  ? "active"
                  : ""
              }
              onClick={() => setView("table")}
              aria-label="Table view"
            >
              <TableRowsOutlined />
            </button>

            <button
              type="button"
              className={
                view === "grid"
                  ? "active"
                  : ""
              }
              onClick={() => setView("grid")}
              aria-label="Grid view"
            >
              <GridViewOutlined />
            </button>
          </div>
        </section>

        {/* Results */}
        <section className="applicant-results">
          <div className="results-context">
            <div>
              <span>APPLICATION RECORDS</span>
              <strong>
                {filteredApplicants.length} results
              </strong>
            </div>

            {hasFilters && (
              <span className="active-filter-indicator">
                Filters active
              </span>
            )}
          </div>

          {filteredApplicants.length === 0 ? (
            <EmptyState
              hasFilters={hasFilters}
              onReset={clearFilters}
            />
          ) : view === "table" ? (
            <TableView
              applicants={filteredApplicants}
              onDelete={handleDelete}
            />
          ) : (
            <CardView
              applicants={filteredApplicants}
              onDelete={handleDelete}
            />
          )}
        </section>

        {loading && (
          <div className="applicant-loading">
            <div className="applicant-loading__spinner" />
            <span>Updating applicant records...</span>
          </div>
        )}
      </div>
    </main>
  );
}